import {Component, effect, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MailboxConfiguratorFacade} from '../../mailbox-configurator.facade';
import {MailboxConfiguratorStore} from '../../mailbox-configurator.store';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {Button} from 'primeng/button';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {Mailbox, MAILBOX_STEPS, MailboxDTO} from '../../mailbox.model';
import {Step, StepItem, StepPanel, Stepper} from 'primeng/stepper';
import {InboundStepComponent} from '../create/inbound-step/inbound-step.component';
import {OutboundStepComponent} from '../create/outbound-step/outbound-step.component';
import {ReviewStepComponent} from '../create/review-step/review-step.component';
import {InboundFormComponent} from '../shared/inbound-form.component';
import {OutboundFormComponent} from '../shared/outbound-form.component';
import {UserProfileStore} from '../../../../core/auth/stores/user-profile.store';
import {MailboxFormComponent} from '../shared/mailbox-form.component';
import {SkeletonCardMailboxComponent} from '../../../../shared/components/skeletons/skeleton-card-mailbox.component';
import {SpinnerComponent} from '../../../../shared/components/skeletons/spinner.component';

@Component({
	selector: 'app-mailbox-configurator',
	standalone: true,
	imports: [CommonModule, Tab, TabPanel, Tabs, TabList, TabPanels, Button, ReactiveFormsModule, Stepper, Step, StepPanel, InboundStepComponent, OutboundStepComponent, ReviewStepComponent, InboundFormComponent, OutboundFormComponent, StepItem, MailboxFormComponent, SkeletonCardMailboxComponent, SpinnerComponent],
	templateUrl: './mailbox-configurator.html',
	styles: `
		.mailbox-grid {
			display: grid;
			grid-template-columns: .4fr 1fr;
			gap: 1rem;
		}
	`
})
export class MailboxConfiguratorComponent {
	private fb = inject(FormBuilder);
	protected facade = inject(MailboxConfiguratorFacade);
	protected store = inject(MailboxConfiguratorStore);
	private profileStore = inject(UserProfileStore)

	mailboxForm = this.fb.group({
		mailbox_name: ['', Validators.required],
		mailbox_address: ['', Validators.required],
	})

	inboundForm = this.fb.group({
		client:            ['', Validators.required],
		email:             ['', [Validators.required, Validators.email]],
		username:          ['', Validators.required],
		password:          [''],
		host:              [''],
		folder:            ['INBOX'],
		imap_port:         [null as number | null],
		protocol:          [''],
		idle_enabled:      [true],
		ssl_enabled:       [true],
		start_tls_enabled: [false],
	});

	outboundForm = this.fb.group({
		client:                    ['', Validators.required],
		email:                     ['', [Validators.required, Validators.email]],
		username:                  ['', Validators.required],
		password:                  [''],
		email_configuration_alias: ['', Validators.required],
		from_name:                 ['', Validators.required],
		from_address:              [''],
		host:                      [''],
		smtp_port:                 [''],
		starttls_enable:           [''],
		mail_debug:                ['false'],
		transport_protocol:        ['smtp'],
		email_auth:                ['true'],
		email_mime_charset:        ['UTF-8'],
	});

	mailboxes = this.facade.mailboxes;
	currentMailbox = this.profileStore.currentMailbox

	constructor() {
		this.facade.loadMailboxes();

		effect(() => {
			const mailbox = this.store.loadedMailbox();
			if (mailbox) this.patchForms(mailbox);
		});
	}

	private patchForms(mailbox: Mailbox) {
		this.patchMailboxForm(mailbox)
		this.patchInboundForm(mailbox)
		this.patchOutboundForm(mailbox)
	}

	private patchInboundForm(mailbox: Mailbox) {
		const inbound = this.store.inbound();

		this.inboundForm.patchValue({
			client:            inbound.client,
			email:             mailbox.mailbox_address,
			username:          inbound.username,
			password:          '',
			host:              inbound.imap_host,
			folder:            inbound.folder,
			imap_port:         inbound.imap_port ? Number(inbound.imap_port) : null,
			protocol:          inbound.protocol,
			idle_enabled:      inbound.idle_enabled,
			ssl_enabled:       inbound.ssl_enabled,
			start_tls_enabled: inbound.start_tls_enabled,
		});
	}

	private patchOutboundForm(mailbox: Mailbox) {
		const outbound = this.store.outbound();

		this.outboundForm.patchValue({
			client:                    outbound.client,
			email:                     mailbox.mailbox_address,
			username:                  outbound.username,
			password:                  '',
			email_configuration_alias: outbound.email_configuration_alias,
			from_name:                 outbound.from_name,
			from_address:              outbound.from_address,
			host:                      outbound.email_host,
			smtp_port:                 outbound.smtp_port,
			starttls_enable:           outbound.starttls_enable,
			mail_debug:                outbound.mail_debug,
			transport_protocol:        outbound.transport_protocol,
			email_auth:                outbound.email_auth,
			email_mime_charset:        outbound.email_mime_charset,
		});
	}

	private patchMailboxForm(mailbox: Mailbox) {
		this.mailboxForm.patchValue({
			mailbox_name: mailbox.mailbox_name,
			mailbox_address: mailbox.mailbox_address,
		});
	}

	private buildInboundPayload(): Record<string, unknown> {
		const v = this.inboundForm.getRawValue();

		const base: Record<string, unknown> = {
			client:   v.client,
			email:    v.email,
			username: v.username,
			folder:   v.folder || 'INBOX',
		};

		if (v.password && v.password.trim() !== '') {
			base['password'] = v.password;
		}

		if (v.client === 'AWS_WORKMAIL') {
			base['host'] = v.host;
		}

		if (v.client === 'CUSTOM') {
			base['host']              = v.host;
			base['imap_port']         = v.imap_port;
			base['protocol']          = v.protocol;
			base['idle_enabled']      = v.idle_enabled;
			base['ssl_enabled']       = v.ssl_enabled;
			base['start_tls_enabled'] = v.start_tls_enabled;
		}

		return base;
	}

	private buildOutboundPayload(): Record<string, unknown> {
		const v = this.outboundForm.getRawValue();

		const base: Record<string, unknown> = {
			client:                    v.client,
			email:                     v.email,
			username:                  v.username,
			email_configuration_alias: v.email_configuration_alias,
			from_name:                 v.from_name,
		};

		if (v.password && v.password.trim() !== '') {
			base['password'] = v.password;
		}

		if (v.from_address && v.from_address.trim() !== '') {
			base['from_address'] = v.from_address;
		}

		if (v.client === 'AWS_WORKMAIL') {
			base['host'] = v.host;
		}

		if (v.client === 'CUSTOM') {
			base['host']               = v.host;
			base['smtp_port']          = v.smtp_port;
			base['starttls_enable']    = v.starttls_enable;
			base['mail_debug']         = v.mail_debug;
			base['transport_protocol'] = v.transport_protocol;
			base['email_auth']         = v.email_auth;
			base['email_mime_charset'] = v.email_mime_charset;
		}

		return base;
	}

	private buildMailboxPayload(): MailboxDTO {
		const v = this.mailboxForm.getRawValue();
		const tenant = this.profileStore.currentTenant();

		if (!tenant) throw new Error('Tenant mancante');

		return {
			mailbox_name: v.mailbox_name!,
			mailbox_address: v.mailbox_address!,
			tenant_id: tenant.tenant_id,
			mailbox_alias: v.mailbox_name!.replace(/\s+/g, '_')
		};
	}

	selectMailbox(mailbox: Mailbox) {
		this.facade.selectMailbox(mailbox.id);
	}

	save() {
		const mailboxPayload = this.buildMailboxPayload();
		const inboundPayload = this.buildInboundPayload();
		const outboundPayload = this.buildOutboundPayload();

		const mailbox = this.store.loadedMailbox();

		if (!mailbox) {
			this.facade.saveMailbox(mailboxPayload);
			return;
		}

		this.facade.updateMailbox(mailbox.id, mailboxPayload);

		this.facade.saveConfigurations(inboundPayload, outboundPayload)
			.subscribe();
	}

	saveMailbox() {
		const payload = this.buildMailboxPayload();
		this.facade.saveMailbox(payload);
	}

	protected readonly MAILBOX_STEPS = MAILBOX_STEPS;
}
